import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Store, Factory, Package, Warehouse, ShoppingCart, GraduationCap, Building, Briefcase, LucideIcon } from 'lucide-react';
import { useJsonData } from '../../hooks/useJsonData';
import { useSafeAnimation, useSafeVariants } from '../ui/SafeMotion';

const iconMap: Record<string, LucideIcon> = {
  Store, Factory, Package, Warehouse, ShoppingCart, GraduationCap, Building, Briefcase
};

interface IndustryItem {
  icon: string;
  name: string;
  description: string;
}

interface IndustriesData {
  industries: IndustryItem[];
}

// Optimized animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

export function Industries() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { data, loading } = useJsonData<IndustriesData>('/data/industries.json');

  // SSR-safe animation props
  const headerAnim = useSafeAnimation(
    { opacity: 0, y: 30 },
    { opacity: 1, y: 0 },
    isInView
  );
  const badgeAnim = useSafeAnimation(
    { opacity: 0, scale: 0.9 },
    { opacity: 1, scale: 1 },
    isInView
  );
  const variants = useSafeVariants(isInView);

  const industries = data?.industries || [];

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white overflow-hidden relative">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-eco-green-50 to-transparent" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={headerAnim.initial}
          animate={headerAnim.animate}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={badgeAnim.initial}
            animate={badgeAnim.animate}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-eco-green-50 border border-eco-green-200 text-eco-green-700 text-sm font-semibold rounded-full mb-6"
          >
            <Briefcase className="w-4 h-4" />
            Industries We Serve
          </motion.span>
          <h2 className="text-4xl sm:text-5xl font-bold text-eco-green-900 mb-4">
            Tailored Solutions for Every Sector
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto text-lg">
            Industry-specific waste management expertise
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="bg-eco-green-50 rounded-2xl p-5 animate-pulse">
                <div className="w-14 h-14 bg-gray-200 rounded-xl mx-auto mb-3" />
                <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto" />
              </div>
            ))}
          </div>
        )}

        {/* Industries Grid */}
        {!loading && (
          <motion.div
            variants={containerVariants}
            initial={variants.initial}
            animate={variants.animate}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4"
          >
            {industries.map((industry) => {
              const IconComponent = iconMap[industry.icon] || Building;
              return (
                <motion.div
                  key={industry.name}
                  variants={itemVariants}
                  whileHover={{ y: -8, scale: 1.05 }}
                  className="group cursor-default"
                >
                  <div className="bg-gradient-to-b from-white to-eco-green-50 rounded-2xl p-5 text-center h-full border border-eco-green-100 hover:border-eco-green-300 hover:shadow-xl hover:shadow-eco-green-100/50 transition-all duration-300">
                    <motion.div 
                      className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-eco-green-500 to-eco-green-700 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-eco-green-300/50 transition-shadow"
                      whileHover={{ rotate: 5 }}
                    >
                      <IconComponent className="w-7 h-7 text-white" />
                    </motion.div>
                    <h3 className="text-sm font-bold text-eco-green-900 mb-1">
                      {industry.name}
                    </h3>
                    <p className="text-xs text-gray-500 hidden sm:block">
                      {industry.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </section>
  );
}
